import React from 'react';

/**
 * Splits a concatenated or multiline error message into individual error strings.
 * Handles:
 * - ASP.NET Identity concatenated errors like:
 *   "Passwords must be at least 8 characters., Passwords must have at least one lowercase ('a'-'z')., Passwords must have at least one uppercase ('A'-'Z')."
 * - Newline-separated messages (\n, \r\n)
 * - Semicolon-separated messages
 * - Arrays of strings/errors
 */
export const splitErrorMessages = (rawMessage: unknown): string[] => {
  if (!rawMessage) return [];

  if (Array.isArray(rawMessage)) {
    return rawMessage.flatMap(splitErrorMessages);
  }

  if (typeof rawMessage !== 'string') {
    if (
      typeof rawMessage === 'object' &&
      rawMessage !== null &&
      'message' in rawMessage
    ) {
      return splitErrorMessages((rawMessage as any).message);
    }

    return [String(rawMessage)];
  }

  const trimmed = rawMessage.trim();
  if (!trimmed) return [];

  let parts: string[] = [];

  // 1. If it has newlines, split by newlines
  if (trimmed.includes('\n')) {
    parts = trimmed.split(/\r?\n+/);
  }
  // 2. ASP.NET Identity / concatenated errors ending with punctuation followed by comma (e.g. "..., ...")
  else if (/(?<=[.!?])\s*,\s*/.test(trimmed)) {
    parts = trimmed.split(/(?<=[.!?])\s*,\s*/);
  }
  // 3. Semicolon separated
  else if (/;\s+/.test(trimmed)) {
    parts = trimmed.split(/;\s+/);
  } else {
    parts = [trimmed];
  }

  return parts
    .map((s) => s.replace(/^[,;\s]+|[,;\s]+$/g, '').trim())
    .filter(Boolean);
};

/**
 * Returns error messages joined by newlines for string-only contexts.
 */
export const formatErrorMessageString = (error: unknown): string => {
  const lines = splitErrorMessages(error);

  return lines.join('\n');
};

/**
 * Formats error message(s) for Ant Design message.error(...)
 * - If multiple messages: returns a multiline layout with bullet points.
 * - If single message: returns a clean wrapped element with max width.
 */
export const formatErrorMessage = (error: unknown): React.ReactNode => {
  let messageText = '';

  if (typeof error === 'string') {
    messageText = error;
  } else if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof (error as any).message === 'string'
  ) {
    messageText = (error as any).message;
  } else {
    messageText = String(error || '');
  }

  const lines = splitErrorMessages(messageText);

  if (lines.length === 0) {
    return messageText;
  }

  if (lines.length === 1) {
    return React.createElement(
      'span',
      {
        style: {
          display: 'inline-block',
          textAlign: 'left',
          maxWidth: '480px',
          wordBreak: 'break-word',
          lineHeight: '1.5',
        },
      },
      lines[0],
    );
  }

  return React.createElement(
    'div',
    {
      style: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        textAlign: 'left',
        maxWidth: '480px',
        wordBreak: 'break-word',
        lineHeight: '1.4',
        padding: '2px 0',
      },
    },
    lines.map((line, idx) =>
      React.createElement(
        'div',
        {
          key: idx,
          style: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
          },
        },
        React.createElement(
          'span',
          {
            style: {
              color: '#ff4d4f',
              fontSize: '14px',
              lineHeight: '1.2',
              userSelect: 'none',
              flexShrink: 0,
            },
          },
          '•',
        ),
        React.createElement('span', null, line),
      ),
    ),
  );
};
